/**
 * scale — reine Skalen-/Zuordnungslogik der UI-Komponentenbibliothek (Story 0.3)
 *
 * Bewusst in einer eigenen Datei: Komponenten-Dateien duerfen laut
 * eslint-plugin-react-refresh nur Komponenten exportieren. Diese Helfer sind
 * ausserdem gut isoliert testbar und von Feature-Screens direkt nutzbar
 * (z. B. um dieselbe Zonen-Einordnung in einer KPI-Karte zu spiegeln).
 */

export type EarnRateZone = 'low' | 'mid' | 'high'

/** Anzahl der Carrier-Farbtokens (--carrier-1..4, Story 0.1). */
export const CARRIER_PALETTE_SIZE = 4

/**
 * Zonengrenzen wie im Design-Prototyp (dashboard.html, `renderGauge`) aus
 * Floor/Basis/Cap abgeleitet: die Floor-Zone endet bei 55 % der Strecke
 * Floor→Basis, die Cap-Zone beginnt bei 55 % der Strecke Basis→Cap.
 */
export function earnRateZoneBounds(floor: number, base: number, cap: number) {
  return {
    lowUpper: floor + (base - floor) * 0.55,
    highLower: cap - (cap - base) * 0.45,
  }
}

/** Einordnung eines Rate-Werts in die drei Zonen. */
export function earnRateZone(
  value: number,
  floor: number,
  base: number,
  cap: number
): EarnRateZone {
  const { lowUpper, highLower } = earnRateZoneBounds(floor, base, cap)
  if (value < lowUpper) return 'low'
  if (value > highLower) return 'high'
  return 'mid'
}

/**
 * Farb-/Muster-Index eines Carrier-Segments (1..CARRIER_PALETTE_SIZE).
 *
 * Welle-2-Review F5: frueher wurde zyklisch weitergezaehlt — ab dem 5. Carrier
 * bekam ein Segment exakt Farbe UND Muster des ersten, war also nicht mehr
 * unterscheidbar. Segmente jenseits der Palette gibt es nicht mehr: der Rest
 * wird zu EINEM neutralen "Andere"-Segment aggregiert
 * (siehe aggregateCarrierSegments). Index-Werte ausserhalb der Palette werden
 * defensiv geklemmt statt still zu kollidieren.
 */
export function carrierSlot(index: number): number {
  if (!Number.isFinite(index) || index < 0) return 1
  return Math.min(Math.floor(index), CARRIER_PALETTE_SIZE - 1) + 1
}

/** Kennung des aggregierten Restsegments — bewusst kein echter Carrier-Key. */
export const CARRIER_OTHER_ID = '__carrier_other__'

export type AggregatableSegment = {
  id: string
  label: string
  value: number
}

export type CarrierAggregation<T extends AggregatableSegment> = {
  /** Die einzeln dargestellten Carrier (hoechstens CARRIER_PALETTE_SIZE). */
  visible: T[]
  /**
   * Summe der zusammengefassten Carrier ab Palettengroesse — 0, wenn nichts
   * aggregiert wurde. Die Anzahl steckt in `otherCount` (fuer das Label).
   */
  otherValue: number
  otherCount: number
}

/**
 * Fasst alles jenseits der Farbpalette zu EINEM Restsegment zusammen.
 *
 * Die Reihenfolge der Eingabe bleibt erhalten (der Aufrufer sortiert, z. B.
 * absteigend nach Volumen) — es werden schlicht die ersten
 * `paletteSize` Segmente einzeln gezeigt und der Rest summiert.
 */
export function aggregateCarrierSegments<T extends AggregatableSegment>(
  segments: T[],
  paletteSize: number = CARRIER_PALETTE_SIZE
): CarrierAggregation<T> {
  if (segments.length <= paletteSize) {
    return { visible: segments, otherValue: 0, otherCount: 0 }
  }
  const rest = segments.slice(paletteSize)
  return {
    visible: segments.slice(0, paletteSize),
    otherValue: rest.reduce((sum, s) => sum + s.value, 0),
    otherCount: rest.length,
  }
}
